import React, { useEffect, useState } from 'react';
import { CloudOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { flushOutbox, onOutboxChange, startOutboxSync } from '../../lib/offlineQueue';

// Tells the tech when they're offline and how much is waiting on the phone, and
// lets them push it the moment they're back in signal.
export default function OfflineBanner({ onSynced, savedAt }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [waiting, setWaiting] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    const off = onOutboxChange(setWaiting);
    startOutboxSync((r) => {
      if (r.sent) { toast.success(`Synced ${r.sent} saved item${r.sent === 1 ? '' : 's'}`); onSynced && onSynced(); }
      if (r.failed) toast.error(`${r.failed} item${r.failed === 1 ? '' : 's'} could not be saved — tell the office`);
    });
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); off(); };
  }, [onSynced]);

  if (online && !waiting && !savedAt) return null;

  const syncNow = async () => {
    setBusy(true);
    const r = await flushOutbox();
    setBusy(false);
    if (r.sent) { toast.success(`Synced ${r.sent} item${r.sent === 1 ? '' : 's'}`); onSynced && onSynced(); }
    else if (r.waiting) toast.error('Still no connection — it will keep trying');
  };

  return (
    <div className={`px-4 py-2 text-xs flex items-center justify-between gap-2 ${online ? 'bg-amber-50 text-amber-900' : 'bg-slate-800 text-white'}`} data-testid="offline-banner">
      <span className="flex items-center gap-1.5">
        <CloudOff className="w-3.5 h-3.5" />
        {online ? '' : 'No signal. '}
        {waiting ? `${waiting} item${waiting === 1 ? '' : 's'} saved on this phone` : ''}
        {!waiting && savedAt ? `Showing jobs saved ${new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
      </span>
      {waiting > 0 && online && (
        <button onClick={syncNow} disabled={busy} className="flex items-center gap-1 font-semibold underline">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />Sync now
        </button>
      )}
    </div>
  );
}
